import React, { useEffect, useRef, useState, useMemo } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/lib/auth";
import { useScheduleData } from "@/hooks/useScheduleData";
import { useDriftWatcher } from "@/hooks/useDriftWatcher";
import { useDoubleBookHandlers } from "@/hooks/useDoubleBookHandlers";
import { useTaskActions } from "@/hooks/useTaskActions";
import { useAiActionDispatcher } from "@/hooks/useAiActionDispatcher";
import { useScheduleKeyboard } from "@/hooks/useScheduleKeyboard";
import { computeProjectDrift, computeBlockDrift } from "@/lib/drift";
import { projectsApi, membersApi, tasksApi } from "@/lib/api";
import { EMPTY_FILTERS, defaultMealDateKey } from "@/lib/scheduleUtils";

import TopBar from "@/components/schedule/TopBar";
import FilterBar from "@/components/schedule/FilterBar";
import FloatingTaskTray from "@/components/schedule/FloatingTaskTray";
import ScheduleGrid from "@/components/schedule/ScheduleGrid";
import CellHistoryDrawer from "@/components/schedule/CellHistoryDrawer";
import AuditLogDrawer from "@/components/schedule/AuditLogDrawer";
import TaskDetailDrawer from "@/components/schedule/TaskDetailDrawer";
import NewProjectDialog from "@/components/schedule/NewProjectDialog";
import CommandPalette from "@/components/schedule/CommandPalette";
import NoteDialog from "@/components/schedule/NoteDialog";
import BulkConfirmDialog from "@/components/schedule/BulkConfirmDialog";
import NewTaskDialog from "@/components/schedule/NewTaskDialog";
import DailyWrapDialog from "@/components/schedule/DailyWrapDialog";
import HelpDrawer from "@/components/schedule/HelpDrawer";
import DriftBanner from "@/components/schedule/DriftBanner";
import EmptyState from "@/components/schedule/EmptyState";
import ProjectSettingsDialog from "@/components/schedule/ProjectSettingsDialog";
import ShareDialog from "@/components/schedule/ShareDialog";
import DaySettingsDialog from "@/components/schedule/DaySettingsDialog";
import DayRolloverDialog, { muteKey as rolloverMuteKey, deriveToday as rolloverDeriveToday } from "@/components/schedule/DayRolloverDialog";
import MealBreakDialog from "@/components/schedule/MealBreakDialog";
import BulkActionBar from "@/components/schedule/BulkActionBar";
import RoomsPanel from "@/components/panels/RoomsPanel";
import CrewPanel from "@/components/panels/CrewPanel";
import EquipmentPanel from "@/components/panels/EquipmentPanel";
import ProjectPanel from "@/components/panels/ProjectPanel";

export default function Schedule() {
  const { user, logout } = useAuth();
  const {
    projects, project, data, loading,
    selectProject, createProject,
    refresh, refreshProjectMeta,
    patchTask, patchBlock, bulkLock, bulkDelete,
    scheduleFloating, scheduleFloatingAtDay,
    addAssignment, addTaskAssignment, removeAssignment,
  } = useScheduleData();

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [density, setDensity] = useState("standard");
  const [groupMode, setGroupMode] = useState(() => {
    try { return localStorage.getItem("ps.groupMode") || "date"; }
    catch (e) { console.warn("ps.groupMode read failed", e); return "date"; }
  });
  const setAndPersistGroupMode = (m) => {
    setGroupMode(m);
    try {
      localStorage.setItem("ps.groupMode", m);
    } catch (e) {
      console.warn("ps.groupMode persist failed", e);
    }
  };
  const [selection, setSelection] = useState([]);
  // Always-fresh selection for HTML5 dragstart handlers (which can run before
  // React flushes setSelection batches when the user clicks then drags fast).
  const selectionRef = useRef([]);
  useEffect(() => { selectionRef.current = selection; }, [selection]);
  const [trayCollapsed, setTrayCollapsed] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTarget, setHistoryTarget] = useState({ block: null, task: null });
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTarget, setDetailTarget] = useState({ task: null, block: null });
  const [openPanel, setOpenPanel] = useState(null);
  const [newProjectOpen, setNewProjectOpen] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("new") === "1"; }
    catch { return false; }
  });
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [noteTarget, setNoteTarget] = useState(null); // task object being noted
  const [bulkAction, setBulkAction] = useState(null); // pending AI bulk-add action awaiting confirmation
  const [newTaskDate, setNewTaskDate] = useState(null); // YYYY-MM-DD when dialog open
  const [newFloatingOpen, setNewFloatingOpen] = useState(false);
  const [dailyWrapOpen, setDailyWrapOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [rolloverOpen, setRolloverOpen] = useState(false);
  const [daySettingsKey, setDaySettingsKey] = useState(null);  // yyyy-mm-dd when open
  const [mealDateKey, setMealDateKey] = useState(null);  // yyyy-mm-dd when MealBreakDialog open
  const [myRole, setMyRole] = useState(null);
  const [now, setNow] = useState(() => new Date());

  // Crew conflicts override: when the user confirmed a double-book, we record
  // the conflict so the grid can show the amber stripe / `⚠ DOUBLE-BOOK` chip.
  // Server-side this is just two assignments — the override flag is local UI.
  const {
    doubleBookKeys,
    handlePatchBlock,
    handleAssignCrew,
    handleAssignCrewToTask,
    handleUnassignCrew,
  } = useDoubleBookHandlers({
    data, patchBlock, addAssignment, addTaskAssignment, removeAssignment,
  });

  // Tick every 30s for live drift readout.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  // Resolve current user's role on the active project (drives owner-only UI).
  useEffect(() => {
    if (!project?.id || !user?.user_id) {
      setMyRole(null);
      return;
    }
    membersApi
      .list(project.id)
      .then((members) => {
        const m = members.find((mm) => mm.user_id === user.user_id && !mm.deleted_at);
        setMyRole(m?.role || null);
      })
      .catch(() => setMyRole(null));
  }, [project?.id, user?.user_id]);

  // Auto-open the AI day-rollover dialog on the first project load of a new
  // day. Mute key persists per project per date so dismissing it once means
  // it stays out of the way until tomorrow.
  useEffect(() => {
    if (!project?.id) return;
    const today = rolloverDeriveToday(project.timezone);
    try {
      if (localStorage.getItem(rolloverMuteKey(project.id, today))) return;
    } catch (e) {
      console.warn("rollover mute read failed", e);
    }
    // Defer so the page paints first.
    const t = setTimeout(() => setRolloverOpen(true), 600);
    return () => clearTimeout(t);
  }, [project?.id, project?.timezone]);

  // ── Derived ──────────────────────────────────────────────
  const floatingTasks = useMemo(
    () => data.tasks.filter((t) => t.is_floating && !t.deleted_at),
    [data.tasks]
  );

  const taskById = useMemo(
    () => Object.fromEntries(data.tasks.map((t) => [t.id, t])),
    [data.tasks]
  );

  const blocksWithDrift = useMemo(() => {
    return data.schedule_blocks.map((b) => {
      const t = taskById[b.task_id];
      const live = computeBlockDrift(b, t, now);
      return live > 0 ? { ...b, drift_min: live } : b;
    });
  }, [data.schedule_blocks, taskById, now]);

  // Collapse groups whose parent has `display_collapsed=true`: hide every
  // child's block and synthesize a virtual parent bar spanning the group's
  // min(start)→max(end). Synthetic blocks are tagged `_synthetic=true` so
  // DnD/lock handlers can route correctly (parent-level only).
  const renderedBlocks = useMemo(() => {
    const childrenByParent = new Map();
    for (const t of data.tasks) {
      if (t.parent_task_id && !t.deleted_at) {
        const arr = childrenByParent.get(t.parent_task_id) || [];
        arr.push(t.id);
        childrenByParent.set(t.parent_task_id, arr);
      }
    }
    const collapsedParents = new Set(
      data.tasks
        .filter((t) => t.display_collapsed && childrenByParent.has(t.id))
        .map((t) => t.id)
    );
    if (collapsedParents.size === 0) return blocksWithDrift;
    const hiddenChildIds = new Set();
    for (const pid of collapsedParents) {
      for (const cid of childrenByParent.get(pid) || []) hiddenChildIds.add(cid);
    }
    const synthByParent = new Map();
    for (const b of blocksWithDrift) {
      if (!hiddenChildIds.has(b.task_id)) continue;
      const t = taskById[b.task_id];
      const parentId = t?.parent_task_id;
      if (!parentId || !collapsedParents.has(parentId)) continue;
      const start = new Date(b.start_at);
      const end = new Date(b.end_at);
      const cur = synthByParent.get(parentId);
      if (!cur) {
        synthByParent.set(parentId, {
          start, end, room_id: b.room_id, is_locked: b.is_locked,
          drift_min: b.drift_min || 0,
        });
      } else {
        if (start < cur.start) cur.start = start;
        if (end > cur.end) cur.end = end;
        cur.drift_min = Math.max(cur.drift_min || 0, b.drift_min || 0);
        cur.is_locked = cur.is_locked && b.is_locked; // group locked only if every child is
      }
    }
    const synthetic = [];
    for (const [pid, s] of synthByParent.entries()) {
      synthetic.push({
        id: `synthetic:${pid}`,
        task_id: pid,
        room_id: s.room_id,
        start_at: s.start.toISOString().replace(/\.\d+Z$/, "Z"),
        end_at: s.end.toISOString().replace(/\.\d+Z$/, "Z"),
        is_locked: !!s.is_locked,
        drift_min: s.drift_min || 0,
        _synthetic: true,
      });
    }
    return [
      ...blocksWithDrift.filter((b) => !hiddenChildIds.has(b.task_id)),
      ...synthetic,
    ];
  }, [blocksWithDrift, data.tasks, taskById]);

  const drift = useMemo(
    () => computeProjectDrift(data.schedule_blocks, taskById, now),
    [data.schedule_blocks, taskById, now]
  );

  // Drift watcher — emits toasts/banners per PRD §9.5.
  const { banners: driftBanners, dismissBanner: dismissDriftBanner } = useDriftWatcher({
    blocks: blocksWithDrift,
    tasksById: taskById,
    enabled: true,
  });

  // ── Handlers ─────────────────────────────────────────────
  const openHistory = (block, task) => {
    setHistoryTarget({ block, task });
    setHistoryOpen(true);
  };
  const openDetail = (task, block) => {
    setDetailTarget({ task, block });
    setDetailOpen(true);
  };
  const handleJumpNow = () => {
    const el = document.querySelector(".now-line");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // Keep the open detail drawer in sync with refreshed data — when refresh()
  // brings back updated task/block, the drawer should see the latest values.
  useEffect(() => {
    setDetailTarget((cur) => {
      if (!cur.task) return cur;
      const t = data.tasks.find((x) => x.id === cur.task.id);
      const b = cur.block ? data.schedule_blocks.find((x) => x.id === cur.block.id) : null;
      if (!t) return cur;
      return { task: t, block: b || cur.block };
    });
  }, [data.tasks, data.schedule_blocks]);

  const onBulkDelete = async () => {
    const ok = await bulkDelete(selection);
    if (ok) setSelection([]);
  };

  // Shared task duplicate / delete / grouping / quick-add handlers.
  const {
    handleDuplicateTask,
    handleDeleteTask,
    handleMoveSelectionToRoom,
    handleGroupTasks,
    handleUnparentSubtasks,
    onQuickAddFloatingForRoom,
    onQuickAddTask,
  } = useTaskActions({ project, data, refresh, patchTask, patchBlock, setSelection });

  // AI palette action dispatcher.
  const dispatchAiAction = useAiActionDispatcher({
    data, patchBlock, patchTask, scheduleFloating, refresh,
    setBulkAction, setPaletteOpen, setFilters, setDetailTarget, setDetailOpen,
  });

  // Global keyboard shortcuts.
  useScheduleKeyboard({
    selectionLength: selection.length,
    detailTaskId: detailTarget?.task?.id,
    detailTaskTitle: detailTarget?.task?.title,
    onBulkDelete,
    setPaletteOpen, setHelpOpen, setSelection, setHistoryOpen, setDetailOpen,
    refresh,
  });

  /** Append a new role name to project.crew_roles and refresh.
   *  Called from the CrewCell / FloatingCrewCell role picker's "+ NEW ROLE" affordance. */
  const handleAddCrewRole = async (newRole) => {
    if (!project) return;
    const current = Array.isArray(project.crew_roles) && project.crew_roles.length
      ? project.crew_roles
      : ["lead", "crew"];
    const next = [...current, newRole];
    await projectsApi.update(project.id, { crew_roles: next });
    await refresh();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-base text-ink-mute mono text-2xs uppercase tracking-widest">
        Loading schedule…
      </div>
    );
  }

  // First-run: signed in but no projects. Show the empty-state hero with options.
  if (!project) {
    return (
      <div className="min-h-screen flex flex-col bg-bg-base text-ink">
        <div className="flex items-center justify-between px-4 h-12 border-b border-line bg-bg-surface">
          <div className="mono text-2xs uppercase tracking-[0.3em] text-amber">PRODUCTION SCHEDULE</div>
          <button onClick={logout} className="btn-ghost" data-testid="empty-logout">
            <X className="w-3 h-3" /> LOG OUT
          </button>
        </div>
        <EmptyState
          user={user}
          onCreate={() => setNewProjectOpen(true)}
          onLoadDemo={async () => {
            try {
              await projectsApi.seedRiverside();
              window.location.reload();
            } catch (e) {
              toast.error(e.response?.data?.detail || "Failed to load demo");
            }
          }}
          onOpenHelp={() => setHelpOpen(true)}
        />
        <NewProjectDialog
          open={newProjectOpen}
          onClose={() => setNewProjectOpen(false)}
          onCreate={async (payload) => {
            const created = await createProject(payload);
            setNewProjectOpen(false);
            return created;
          }}
        />
        <HelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-bg-base text-ink">
      <TopBar
        project={project}
        projects={projects}
        currentProjectId={project?.id}
        onSelectProject={selectProject}
        onNewProject={() => setNewProjectOpen(true)}
        drift={drift}
        density={density}
        onDensity={setDensity}
        onLogout={logout}
        user={user}
        onJumpNow={handleJumpNow}
        onOpenAI={() => setPaletteOpen(true)}
        onOpenDailyWrap={() => setDailyWrapOpen(true)}
        onOpenHelp={() => setHelpOpen(true)}
        onOpenSettings={() => setSettingsOpen(true)}
        onOpenShare={() => setShareOpen(true)}
        onOpenAudit={() => setAuditOpen(true)}
        myRole={myRole}
        onOpenPanel={setOpenPanel}
        groupMode={groupMode}
        onGroupMode={setAndPersistGroupMode}
      />
      <FilterBar
        rooms={data.rooms}
        crew={data.crew_members}
        filters={filters}
        onChange={setFilters}
        groupMode={groupMode}
      />
      <DriftBanner
        banners={driftBanners}
        onDismiss={dismissDriftBanner}
        onOpenTask={(blockId) => {
          const b = data.schedule_blocks.find((x) => x.id === blockId);
          if (b) openDetail(taskById[b.task_id], b);
        }}
      />
      {groupMode !== "room" && (
        <FloatingTaskTray
          floatingTasks={floatingTasks}
          collapsed={trayCollapsed}
          onToggle={() => setTrayCollapsed((c) => !c)}
          onNewFloating={() => setNewFloatingOpen(true)}
          onAddMeal={() => setMealDateKey(defaultMealDateKey(project))}
          onClickTask={(task) => openDetail(task, null)}
          onDuplicateTask={handleDuplicateTask}
          onDeleteTask={handleDeleteTask}
        />
      )}
      <ScheduleGrid
        project={project}
        rooms={data.rooms}
        crew={data.crew_members}
        tasks={data.tasks}
        blocks={renderedBlocks}
        assignments={data.assignments}
        blockers={data.blockers}
        filters={filters}
        density={density}
        selection={selection}
        setSelection={setSelection}
        onPatchBlock={handlePatchBlock}
        onPatchTask={patchTask}
        onAssignCrew={handleAssignCrew}
        onAssignCrewToTask={handleAssignCrewToTask}
        onUnassignCrew={handleUnassignCrew}
        doubleBookKeys={doubleBookKeys}
        onOpenHistory={openHistory}
        onSelectTask={openDetail}
        floatingTasks={floatingTasks}
        schedFilter={filters.schedFilter || "all"}
        onEditNotes={(task) => setNoteTarget(task)}
        onDuplicateTask={handleDuplicateTask}
        onDeleteTask={handleDeleteTask}
        onNewTaskForDay={(dateKey) => setNewTaskDate(dateKey)}
        onQuickAddTask={onQuickAddTask}
        onNewFloatingForRoom={(/* roomId */) => setNewFloatingOpen(true)}
        onQuickAddFloatingForRoom={onQuickAddFloatingForRoom}
        onDropFloating={scheduleFloating}
        onDropFloatingForDay={scheduleFloatingAtDay}
        onMoveSelectionToRoom={handleMoveSelectionToRoom}
        onGroupTasks={handleGroupTasks}
        onUnparentSubtasks={handleUnparentSubtasks}
        selectionRef={selectionRef}
        onOpenDaySettings={(dateKey) => setDaySettingsKey(dateKey)}
        groupMode={groupMode}
        onAddCrewRole={handleAddCrewRole}
      />
      {selection.length > 1 && (
        <BulkActionBar
          count={selection.length}
          onClear={() => setSelection([])}
          onLock={() => bulkLock(selection, true)}
          onUnlock={() => bulkLock(selection, false)}
          onDelete={onBulkDelete}
        />
      )}
      <CellHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        block={historyTarget.block}
        task={historyTarget.task}
      />
      <TaskDetailDrawer
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        task={detailTarget.task}
        block={detailTarget.block}
        room={data.rooms.find((r) => r.id === detailTarget.block?.room_id)}
        blockers={data.blockers.filter((b) => b.task_id === detailTarget.task?.id)}
        subtasks={data.tasks.filter((t) => t.parent_task_id === detailTarget.task?.id && !t.deleted_at)}
        crew={data.crew_members}
        assignments={data.assignments.filter((a) => a.schedule_block_id === detailTarget.block?.id)}
        equipment={data.equipment}
        onMutated={refresh}
      />

      <RoomsPanel open={openPanel === "rooms"} onClose={() => setOpenPanel(null)} projectId={project?.id} rooms={data.rooms} onMutated={refresh} />
      <CrewPanel open={openPanel === "crew"} onClose={() => setOpenPanel(null)} projectId={project?.id} crew={data.crew_members} rooms={data.rooms} onMutated={refresh} />
      <EquipmentPanel open={openPanel === "equipment"} onClose={() => setOpenPanel(null)} projectId={project?.id} equipment={data.equipment} onMutated={refresh} />
      <ProjectPanel open={openPanel === "project"} onClose={() => setOpenPanel(null)} project={project} onMutated={refreshProjectMeta} />

      <NewProjectDialog
        open={newProjectOpen}
        onClose={() => setNewProjectOpen(false)}
        onCreate={createProject}
      />

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        projectId={project?.id}
        onAction={dispatchAiAction}
      />

      <NoteDialog
        open={!!noteTarget}
        title={noteTarget?.title || ""}
        initialValue={noteTarget?.notes}
        placeholder="Add task notes — site requirements, special instructions, equipment quirks…"
        onSave={async (v) => {
          if (!noteTarget) return;
          await patchTask(noteTarget.id, { notes: v });
        }}
        onClose={() => setNoteTarget(null)}
      />

      <BulkConfirmDialog
        open={!!bulkAction}
        action={bulkAction}
        rooms={data.rooms}
        projectId={project?.id}
        onClose={() => setBulkAction(null)}
        onCommitted={refresh}
      />

      <NewTaskDialog
        open={!!newTaskDate || newFloatingOpen}
        dateKey={newTaskDate}
        floating={newFloatingOpen}
        projectId={project?.id}
        rooms={data.rooms}
        blocks={data.schedule_blocks}
        onClose={() => { setNewTaskDate(null); setNewFloatingOpen(false); }}
        onCreated={refresh}
      />

      <DailyWrapDialog
        open={dailyWrapOpen}
        onClose={() => setDailyWrapOpen(false)}
        projectId={project?.id}
        defaultDateKey={null}
      />

      <HelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} />

      <ProjectSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        project={project}
        myRole={myRole}
        onMutated={refreshProjectMeta}
        onProjectDeleted={() => window.location.reload()}
        onProjectDuplicated={(p) => {
          if (p?.id) {
            selectProject(p.id);
            toast.message("Switched to duplicate", { description: p.name });
          }
        }}
      />

      <ShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        project={project}
        myRole={myRole}
        myUserId={user?.user_id}
      />

      <AuditLogDrawer
        open={auditOpen}
        onClose={() => setAuditOpen(false)}
        projectId={project?.id}
        taskById={taskById}
        blockById={Object.fromEntries(data.schedule_blocks.map((b) => [b.id, b]))}
        onRestored={refresh}
      />

      <DayRolloverDialog
        open={rolloverOpen}
        onClose={() => setRolloverOpen(false)}
        projectId={project?.id}
        projectTimezone={project?.timezone}
        onApplied={refresh}
      />

      <DaySettingsDialog
        open={!!daySettingsKey}
        dateKey={daySettingsKey}
        project={project}
        crew={data.crew_members}
        onClose={() => setDaySettingsKey(null)}
        onChanged={() => refresh()}
      />

      <MealBreakDialog
        open={!!mealDateKey}
        dateKey={mealDateKey}
        project={project}
        crew={data.crew_members}
        rooms={data.rooms}
        onClose={() => setMealDateKey(null)}
        onCreated={() => refresh()}
      />
    </div>
  );
}
